import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { BodyText, Button, FlexContainer, FlexItem } from '@placer-ui/components';
import { RequestPropertyFormProps } from 'features/my-zone/support-requests/components/support-requests-modals/modals/request-new-location/request-property/types';
import {
    getFlagsMap,
    getVenueFlagInfoPublicPOITooltip,
} from 'ui-components/venue-status-indicator/v2/venue-status-indicator-v2';
import { VenueFlags } from 'core/entities';
import { ControlledTextArea } from 'features/my-zone/support-requests/components/support-requests-modals/components/inputs/controlled-textarea';
import { getEntityReportLink } from 'features/my-zone/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/support-request-results-modal/utils';
import { InfoBox } from 'features/my-zone/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/support-request-results-modal/components/ask-for-review/components/info-box/info-box';
import isEmpty from 'lodash/isEmpty';
import { SendSupportCSOwnerEmail } from 'features/my-zone/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/support-request-results-modal/components/ask-for-review/components/send-support-cs-owner-email/send-support-cs-owner-email';

export type ReasonForNearFormValues = {
    note: string;
};

const ReasonForNearByAskForReview = ({ onNextHandler }: RequestPropertyFormProps) => {
    const formController = useForm<ReasonForNearFormValues>({
        defaultValues: {
            note: '',
        },
        mode: 'onBlur',
    });

    const {
        formState: { dirtyFields },
    } = formController;

    const handleSubmit = (data: ReasonForNearFormValues) => {
        onNextHandler?.({ ...data, isDirty: !isEmpty(dirtyFields) });
    };

    return (
        <FormProvider {...formController}>
            <form onSubmit={(ev) => ev.preventDefault()} style={{ height: '100%' }}>
                <FlexContainer flexDirection="column" gap="16px" height="100%">
                    <FlexContainer flexDirection="column" gap="16px" height="100%">
                        <div>
                            <ControlledTextArea
                                name="note"
                                placeholder="If you believe it's incorrect, provide supporting evidence for a second review."
                                initialHeight={140}
                                required
                                maxLength={1000}
                                testId="report-mistake-note"
                            />
                        </div>
                        <BodyText color="gray" size="small">
                            *We’ll check the data, but it may remain as nearby.
                        </BodyText>
                    </FlexContainer>

                    <FlexItem alignSelf="flex-end">
                        <Button
                            size="small"
                            disabled={!formController.formState.isValid}
                            onClick={formController.handleSubmit(handleSubmit)}
                            testId="submit-button"
                            variant="primary-gray"
                        >
                            Done
                        </Button>
                    </FlexItem>
                </FlexContainer>
            </form>
        </FormProvider>
    );
};

export const ReasonForNearBy = ({ onNextHandler, item }: RequestPropertyFormProps) => {
    const [nearbyType, setNearbyType] = useState<'ask-for-review' | 'report-mistake' | undefined>(
        undefined,
    );

    const flagsConfig = getFlagsMap();

    const flag: VenueFlags =
        item!.flag_reasons[0] === 'unavailable' ? 'unknown_flag' : item!.flag_reasons[0];
    const reason = flagsConfig[flag];
    const description = getVenueFlagInfoPublicPOITooltip(item?.flag_reasons[0], item.entity_name);

    // TODO - handle isReviewed logic after internal resolver has that information - PLAC-64785
    const isReviewed = true;

    return (
        <>
            {!nearbyType && (
                <FlexContainer justifyContent="space-between" flexDirection="column" height="100%">
                    <FlexContainer flexDirection="column" gap={16}>
                        <div data-testid="reason-for-nearby-container">
                            <BodyText element="p" variant="bold">
                                Reason: {reason}
                            </BodyText>
                            <BodyText element="p">{description}</BodyText>
                        </div>

                        <InfoBox
                            type="info"
                            testId="reason-for-nearby-info-box"
                            title="Placer's Nearby Activity Reports"
                            message="You can already view visitation insights for this property, based on nearby visits within a set distance from the property. "
                            linkText="Learn More"
                            elevioInlineId={537}
                            icon="Lightbulb"
                        />
                    </FlexContainer>
                    <FlexItem alignSelf="flex-end">
                        <FlexContainer gap={8}>
                            {isReviewed ? (
                                <Button
                                    onClick={() => setNearbyType('report-mistake')}
                                    testId="report-mistake-button"
                                    variant="secondary-gray"
                                    size="small"
                                >
                                    Report a Mistake
                                </Button>
                            ) : (
                                <Button
                                    onClick={() => setNearbyType('ask-for-review')}
                                    testId="ask-for-review-button"
                                    variant="secondary-gray"
                                    size="small"
                                >
                                    Ask for Review
                                </Button>
                            )}

                            <Button
                                onClick={() => {
                                    const link = getEntityReportLink(item);

                                    window.open(link, '_blank');
                                }}
                                testId="nearby-activity-button"
                                variant="primary-gray"
                                size="small"
                            >
                                Open Nearby Activity
                            </Button>
                        </FlexContainer>
                    </FlexItem>
                </FlexContainer>
            )}
            {nearbyType === 'ask-for-review' && (
                <ReasonForNearByAskForReview onNextHandler={onNextHandler} />
            )}
            {nearbyType === 'report-mistake' && (
                <SendSupportCSOwnerEmail entity={item} onNextHandler={onNextHandler} />
            )}
        </>
    );
};
